import { useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { FrameOrderRow } from './FrameOrderRow';
import type { FrameOrder, SortField, SortOrder } from '../../types/frameOrders';

interface FrameOrderTableProps {
    orders: FrameOrder[];
    sortBy: SortField;
    sortOrder: SortOrder;
    onSort: (field: SortField) => void;
    canEdit: boolean;
    onCommentAdded: () => void;
    assigneeOptions: string[];
}

interface ColumnDef {
    key: SortField | null;
    label: string;
    className?: string;
    /** Tooltip on the header, for labels that need more than the column width allows. */
    title?: string;
}

const COLUMNS: ColumnDef[] = [
    { key: null, label: '', className: 'w-8' },
    { key: 'companyCode', label: 'Co.' },
    { key: 'documentNr', label: 'Document' },
    { key: 'classificationCode', label: 'Cls' },
    { key: 'customerName', label: 'Customer' },
    { key: 'productName', label: 'Product' },
    { key: 'postingOrOrderDate', label: 'Posting...' },
    { key: 'dateFirstCall', label: 'First Call' },
    { key: 'dateLastCall', label: 'Last Call' },
    { key: 'quantity', label: 'Qty', className: 'text-right' },
    { key: 'outstandingQuantity', label: 'Outst. Qty', className: 'text-right' },
    {
        key: 'qtyOnStock',
        label: 'Stock (all loc.)',
        className: 'text-right',
        title: 'Stock of this product across all locations of the company - not the stock at the location of this line.',
    },
    { key: null, label: '', className: 'w-12' },
];

export function FrameOrderTable({ orders, sortBy, sortOrder, onSort, canEdit, onCommentAdded, assigneeOptions }: FrameOrderTableProps) {
    const [expandedId, setExpandedId] = useState<string | null>(null);

    const toggleExpand = (documentId: string) => {
        setExpandedId(prev => prev === documentId ? null : documentId);
    };

    const SortIcon = ({ field }: { field: SortField }) => {
        if (sortBy !== field) return <span className="ml-1 text-gray-300">&#8597;</span>;
        return sortOrder === 'ASC'
            ? <ChevronUp className="ml-0.5 inline h-3.5 w-3.5" />
            : <ChevronDown className="ml-0.5 inline h-3.5 w-3.5" />;
    };

    return (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        {COLUMNS.map((col, i) => (
                            <th
                                key={i}
                                className={`px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 ${col.className || ''} ${col.key ? 'cursor-pointer select-none hover:bg-gray-100' : ''}`}
                                title={col.title}
                                onClick={() => col.key && onSort(col.key)}
                            >
                                {col.label}
                                {col.key && <SortIcon field={col.key} />}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {orders.length === 0 ? (
                        <tr>
                            <td colSpan={COLUMNS.length} className="px-3 py-12 text-center text-sm text-gray-500">
                                No frame orders found.
                            </td>
                        </tr>
                    ) : (
                        orders.map(order => (
                            <FrameOrderRow
                                key={order.DocumentID}
                                order={order}
                                isExpanded={expandedId === order.DocumentID}
                                onToggle={() => toggleExpand(order.DocumentID)}
                                canEdit={canEdit}
                                onCommentAdded={onCommentAdded}
                                assigneeOptions={assigneeOptions}
                            />
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
}
